import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, User, Image, FileText, CheckCircle, XCircle, Eye, Shield, Star, MapPin, Phone, Mail, Building, Home, Save, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/hooks/useAdminAuth";

interface TradesmanProfile {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  company_name: string;
  job_categories: string[];
  location: string;
  address: string;
  average_rating: number;
  total_reviews: number;
  insurance_approved: boolean;
  is_suspended: boolean;
  created_at: string;
  platform_source: string;
}

interface PortfolioPhoto {
  id: string;
  file_name: string;
  file_path: string;
  description: string;
  status: string;
  created_at: string;
  tradesman_id: string;
}

interface InsuranceCertificate {
  id: string;
  file_name: string;
  file_path: string;
  expiry_date: string;
  status: string;
  created_at: string;
  tradesman_id: string;
  rejection_reason?: string;
}

export default function AdminTradesmanManagement() {
  const [tradesmen, setTradesmen] = useState<TradesmanProfile[]>([]);
  const [portfolioPhotos, setPortfolioPhotos] = useState<PortfolioPhoto[]>([]);
  const [insuranceCerts, setInsuranceCerts] = useState<InsuranceCertificate[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTradesman, setSelectedTradesman] = useState<TradesmanProfile | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<PortfolioPhoto | null>(null);
  const [selectedCert, setSelectedCert] = useState<InsuranceCertificate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState<TradesmanProfile | null>(null);
  const { toast } = useToast();
  const { isAdmin, isLoading: authLoading } = useAdminAuth();

  useEffect(() => {
    if (!authLoading && isAdmin) {
      fetchData();
    }
  }, [authLoading, isAdmin]);

  const fetchData = async () => {
    if (!isAdmin) return;
    setIsLoading(true);

    // 1) Always fetch tradesmen profiles first
    try {
      const { data: tradesmenData, error: tradesmenError } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_type", "tradesman")
        .order("created_at", { ascending: false });

      if (tradesmenError) throw tradesmenError;
      setTradesmen(tradesmenData || []);
    } catch (error: any) {
      console.error("Error fetching tradesman data:", error);
      toast({
        title: "Error",
        description: "Failed to fetch tradesmen profiles.",
        variant: "destructive",
      });
    }

    // 2) Try to fetch portfolio photos (best effort)
    try {
      const { data: photosData, error: photosError } = await supabase
        .from("portfolio_photos")
        .select("*")
        .order("created_at", { ascending: false });

      if (photosError) throw photosError;
      setPortfolioPhotos(photosData || []);
    } catch (error: any) {
      console.warn("Portfolio photos not available:", error?.message || error);
      setPortfolioPhotos([]);
    }

    // 3) Try to fetch insurance certificates (best effort)
    try {
      const { data: certsData, error: certsError } = await supabase
        .from("insurance_certificates")
        .select("*")
        .order("created_at", { ascending: false });

      if (certsError) throw certsError;
      setInsuranceCerts(certsData || []);
    } catch (error: any) {
      console.warn("Insurance certificates not available:", error?.message || error);
      setInsuranceCerts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoApproval = async (photoId: string, status: 'approved' | 'rejected', rejectionReason?: string) => {
    try {
      const { error } = await supabase
        .from("portfolio_photos")
        .update({ 
          status,
          approved_at: status === 'approved' ? new Date().toISOString() : null,
          rejection_reason: rejectionReason || null
        })
        .eq("id", photoId);

      if (error) throw error;

      toast({
        title: "Photo Updated",
        description: `Portfolio photo has been ${status}.`,
      });

      fetchData();
      setSelectedPhoto(null);
    } catch (error: any) {
      console.error("Error updating photo:", error);
      toast({
        title: "Error",
        description: "Failed to update photo status.",
        variant: "destructive",
      });
    }
  };

  const handleInsuranceApproval = async (certId: string, status: 'approved' | 'rejected', rejectionReason?: string) => {
    try {
      const { error } = await supabase
        .from("insurance_certificates")
        .update({ 
          status,
          approved_at: status === 'approved' ? new Date().toISOString() : null,
          rejection_reason: rejectionReason || null
        })
        .eq("id", certId);

      if (error) throw error;

      // If approved, also update the tradesman's profile
      if (status === 'approved') {
        const cert = insuranceCerts.find(c => c.id === certId);
        if (cert) {
          await supabase
            .from("profiles")
            .update({ insurance_approved: true })
            .eq("id", cert.tradesman_id);
        }
      }

      toast({
        title: "Insurance Certificate Updated",
        description: `Insurance certificate has been ${status}.`,
      });

      fetchData();
      setSelectedCert(null);
    } catch (error: any) {
      console.error("Error updating insurance certificate:", error);
      toast({
        title: "Error",
        description: "Failed to update insurance certificate status.",
        variant: "destructive",
      });
    }
  };

  const validateUKPhone = (phone: string) => {
    // UK phone number regex - accepts landline and mobile formats
    const ukPhoneRegex = /^(?:(?:\+44)|(?:0))(?:(?:7[0-9]{9})|(?:1[0-9]{9})|(?:2[0-9]{9})|(?:3[0-9]{9})|(?:8[0-8][0-9]{8})|(?:9[0-9]{9}))$/;
    return ukPhoneRegex.test(phone.replace(/\s/g, ''));
  };

  const handleEditTradesman = (tradesman: TradesmanProfile) => {
    setSelectedTradesman(tradesman);
    setEditForm({ ...tradesman });
    setIsEditMode(true);
  };

  const handleSaveChanges = async () => {
    if (!editForm) return;
    
    // Validate phone number
    if (editForm.phone && editForm.phone !== "PHONE_REQUIRED" && !validateUKPhone(editForm.phone)) {
      toast({
        title: "Invalid phone number",
        description: "Please enter a valid UK phone number (e.g., 07123456789 or 01234567890).",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: editForm.full_name,
          company_name: editForm.company_name,
          email: editForm.email,
          phone: editForm.phone,
          address: editForm.address,
          location: editForm.location,
          job_categories: editForm.job_categories,
          is_suspended: editForm.is_suspended,
          insurance_approved: editForm.insurance_approved,
        })
        .eq("id", editForm.id);

      if (error) throw error;

      toast({
        title: "Tradesman Updated",
        description: "Tradesman details have been successfully updated.",
      });

      fetchData();
      setIsEditMode(false);
      setSelectedTradesman(null);
      setEditForm(null);
    } catch (error: any) {
      console.error("Error updating tradesman:", error);
      toast({
        title: "Error",
        description: "Failed to update tradesman details.",
        variant: "destructive",
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditForm(null);
  };

  const toggleTradesmanSuspension = async (tradesman: TradesmanProfile) => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ is_suspended: !tradesman.is_suspended })
        .eq("id", tradesman.id);

      if (error) throw error;

      toast({
        title: "Tradesman Status Updated",
        description: `${tradesman.full_name} has been ${!tradesman.is_suspended ? 'suspended' : 'reactivated'}.`,
      });

      fetchData();
    } catch (error: any) {
      console.error("Error updating tradesman status:", error);
      toast({
        title: "Error",
        description: "Failed to update tradesman status.",
        variant: "destructive",
      });
    }
  };

  const filteredTradesmen = tradesmen.filter(tradesman =>
    tradesman.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tradesman.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tradesman.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tradesman.job_categories?.some(category => 
      category.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      case 'pending':
      default:
        return <Badge variant="outline">Pending Review</Badge>;
    }
  };

  if (authLoading || isLoading) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-destructive" />
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              You don't have permission to access Tradesman Management. Admin privileges are required.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Tradesman Management</h2>
        <Button onClick={fetchData} variant="outline">
          Refresh Data
        </Button>
      </div>

      <Tabs defaultValue="profiles" className="space-y-4">
        <div className="w-full overflow-x-auto pb-2">
          <TabsList className="inline-flex w-max min-w-full">
            <TabsTrigger value="profiles" className="whitespace-nowrap">Tradesman Profiles</TabsTrigger>
            <TabsTrigger value="photos" className="whitespace-nowrap">Portfolio Photos</TabsTrigger>
            <TabsTrigger value="insurance" className="whitespace-nowrap">Insurance Certificates</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="profiles" className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tradesmen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tradesman</TableHead>
                    <TableHead>Company & Trade</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead>Insurance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTradesmen.map((tradesman) => (
                    <TableRow key={tradesman.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{tradesman.full_name || "Unnamed"}</div>
                          <div className="text-sm text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {tradesman.location || "No location"}
                          </div>
                          <Badge variant="outline" className="text-xs mt-1">
                            {tradesman.platform_source === 'movable' ? 'Mover' : 'Tradesman'}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                          <div>
                            <div className="font-medium">{tradesman.company_name || "No company name"}</div>
                            <div className="text-sm text-muted-foreground">
                              {tradesman.job_categories?.join(", ") || "No categories"}
                            </div>
                          </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="w-3 h-3" />
                            {tradesman.email}
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="w-3 h-3" />
                            {tradesman.phone === "PHONE_REQUIRED" ? "Not provided" : tradesman.phone}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-400" />
                          <span>{tradesman.average_rating?.toFixed(1) || "0.0"}</span>
                          <span className="text-sm text-muted-foreground">
                            ({tradesman.total_reviews || 0} reviews)
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {tradesman.insurance_approved ? (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Approved
                          </Badge>
                        ) : (
                          <Badge variant="outline">Not Approved</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {tradesman.is_suspended ? (
                          <Badge className="bg-red-100 text-red-800">Suspended</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-800">Active</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditTradesman(tradesman)}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View & Edit
                          </Button>
                          <Button
                            size="sm"
                            variant={tradesman.is_suspended ? "default" : "destructive"}
                            onClick={() => toggleTradesmanSuspension(tradesman)}
                          >
                            {tradesman.is_suspended ? "Reactivate" : "Suspend"}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="photos" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Portfolio Photos Pending Review</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {portfolioPhotos.filter(photo => photo.status === 'pending').map((photo) => {
                  const tradesman = tradesmen.find(t => t.id === photo.tradesman_id);
                  return (
                    <Card key={photo.id} className="cursor-pointer" onClick={() => setSelectedPhoto(photo)}>
                      <CardHeader>
                        <CardTitle className="text-sm">{tradesman?.full_name || "Unknown"}</CardTitle>
                        <p className="text-xs text-muted-foreground">{tradesman?.company_name}</p>
                      </CardHeader>
                      <CardContent>
                        <img 
                          src={`https://asrkgtsboqenyuetcjuj.supabase.co/storage/v1/object/public/portfolio-photos/${photo.file_path}`}
                          alt={photo.description || "Portfolio photo"}
                          className="w-full h-32 object-cover rounded mb-2"
                        />
                        <p className="text-sm">{photo.description}</p>
                        <div className="mt-2">{getStatusBadge(photo.status)}</div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="insurance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Insurance Certificates Pending Review</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tradesman</TableHead>
                    <TableHead>File Name</TableHead>
                    <TableHead>Expiry Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {insuranceCerts.filter(cert => cert.status === 'pending').map((cert) => {
                    const tradesman = tradesmen.find(t => t.id === cert.tradesman_id);
                    return (
                      <TableRow key={cert.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{tradesman?.full_name || "Unknown"}</div>
                            <div className="text-sm text-muted-foreground">{tradesman?.company_name}</div>
                          </div>
                        </TableCell>
                        <TableCell>{cert.file_name}</TableCell>
                        <TableCell>{new Date(cert.expiry_date).toLocaleDateString()}</TableCell>
                        <TableCell>{getStatusBadge(cert.status)}</TableCell>
                        <TableCell>{new Date(cert.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedCert(cert)}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Tradesman Details Dialog */}
      <Dialog open={!!selectedTradesman} onOpenChange={() => {
        setSelectedTradesman(null);
        setIsEditMode(false);
        setEditForm(null);
      }}>
        <DialogContent className="w-[95vw] max-w-2xl mx-2 sm:mx-auto">
          <DialogHeader>
            <DialogTitle>{isEditMode ? "Edit Tradesman Details" : "Tradesman Details"}</DialogTitle>
          </DialogHeader>
          {selectedTradesman && (
            <div className="space-y-6">
              {isEditMode && editForm ? (
                // Edit Mode
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-name">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="edit-name"
                        value={editForm.full_name || ""}
                        onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                        className="pl-10"
                        placeholder="Enter full name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="edit-company">Company Name</Label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="edit-company"
                        value={editForm.company_name || ""}
                        onChange={(e) => setEditForm({...editForm, company_name: e.target.value})}
                        className="pl-10"
                        placeholder="Enter company name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="edit-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="edit-email"
                        type="email"
                        value={editForm.email || ""}
                        onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                        className="pl-10"
                        placeholder="Enter email"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="edit-phone">Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="edit-phone"
                        type="tel"
                        value={editForm.phone === "PHONE_REQUIRED" ? "" : editForm.phone || ""}
                        onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                        className="pl-10"
                        placeholder="e.g., 07123456789"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="edit-address">Address</Label>
                    <div className="relative">
                      <Home className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="edit-address"
                        value={editForm.address || ""}
                        onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                        className="pl-10"
                        placeholder="Enter full address"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="edit-location">Location (Service Area)</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="edit-location"
                        value={editForm.location || ""}
                        onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                        className="pl-10"
                        placeholder="Enter service area"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground">Full Name</h4>
                    <p className="text-lg font-medium">{selectedTradesman.full_name || "Not provided"}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground">Company Name</h4>
                    <p className="text-lg font-medium">{selectedTradesman.company_name || "Not provided"}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground">Email</h4>
                    <p className="text-lg">{selectedTradesman.email}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground">Phone</h4>
                    <p className="text-lg">{selectedTradesman.phone === "PHONE_REQUIRED" ? "Not provided" : selectedTradesman.phone}</p>
                  </div>
                  <div className="md:col-span-2">
                    <h4 className="font-medium text-sm text-muted-foreground">Address</h4>
                    <p className="text-lg">{selectedTradesman.address || "Not provided"}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground">Location (Service Area)</h4>
                    <p className="text-lg">{selectedTradesman.location || "Not specified"}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground">Rating & Reviews</h4>
                    <div className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-yellow-400" />
                      <span className="text-lg font-medium">{selectedTradesman.average_rating?.toFixed(1) || "0.0"}</span>
                      <span className="text-muted-foreground">({selectedTradesman.total_reviews || 0} reviews)</span>
                    </div>
                  </div>
                </div>
              )}
              
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">Job Categories</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedTradesman.job_categories?.length > 0 ? (
                      selectedTradesman.job_categories.map((category, index) => (
                        <Badge key={index} variant="outline">{category}</Badge>
                      ))
                    ) : (
                      <p className="text-muted-foreground">No categories specified</p>
                    )}
                  </div>
                </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground">Insurance Status</h4>
                  {selectedTradesman.insurance_approved ? (
                    <Badge className="bg-green-100 text-green-800">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Approved
                    </Badge>
                  ) : (
                    <Badge variant="outline">Not Approved</Badge>
                  )}
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground">Account Status</h4>
                  {selectedTradesman.is_suspended ? (
                    <Badge className="bg-red-100 text-red-800">Suspended</Badge>
                  ) : (
                    <Badge className="bg-green-100 text-green-800">Active</Badge>
                  )}
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground">Member Since</h4>
                  <p className="text-lg">{new Date(selectedTradesman.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                {isEditMode ? (
                  <>
                    <Button onClick={handleSaveChanges} className="bg-green-600 hover:bg-green-700">
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </Button>
                    <Button variant="outline" onClick={handleCancelEdit}>
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button onClick={() => setIsEditMode(true)} variant="default">
                      Edit Details
                    </Button>
                    <Button
                      variant={selectedTradesman.is_suspended ? "default" : "destructive"}
                      onClick={() => {
                        toggleTradesmanSuspension(selectedTradesman);
                        setSelectedTradesman(null);
                      }}
                    >
                      {selectedTradesman.is_suspended ? "Reactivate Account" : "Suspend Account"}
                    </Button>
                    <Button variant="outline" onClick={() => setSelectedTradesman(null)}>
                      Close
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo Review Dialog */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="w-[95vw] max-w-2xl mx-2 sm:mx-auto">
          <DialogHeader>
            <DialogTitle>Review Portfolio Photo</DialogTitle>
          </DialogHeader>
          {selectedPhoto && (
            <div className="space-y-4">
              <img 
                src={`https://asrkgtsboqenyuetcjuj.supabase.co/storage/v1/object/public/portfolio-photos/${selectedPhoto.file_path}`}
                alt={selectedPhoto.description || "Portfolio photo"}
                className="w-full h-64 object-cover rounded"
              />
              <div>
                <h4 className="font-medium">Description:</h4>
                <p>{selectedPhoto.description || "No description provided"}</p>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={() => handlePhotoApproval(selectedPhoto.id, 'approved')}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                <Button 
                  onClick={() => handlePhotoApproval(selectedPhoto.id, 'rejected', 'Does not meet quality standards')}
                  variant="destructive"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Insurance Certificate Review Dialog */}
      <Dialog open={!!selectedCert} onOpenChange={() => setSelectedCert(null)}>
        <DialogContent className="w-[95vw] max-w-2xl mx-2 sm:mx-auto">
          <DialogHeader>
            <DialogTitle>Review Insurance Certificate</DialogTitle>
          </DialogHeader>
          {selectedCert && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium">File Name:</h4>
                  <p>{selectedCert.file_name}</p>
                </div>
                <div>
                  <h4 className="font-medium">Expiry Date:</h4>
                  <p>{new Date(selectedCert.expiry_date).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="border p-4 rounded bg-gray-50">
                <p className="text-sm text-muted-foreground mb-2">Click to download and review the certificate:</p>
                <Button 
                  variant="outline"
                  onClick={async () => {
                    console.log('=== ADMIN CERTIFICATE DOWNLOAD ===');
                    console.log('Selected certificate:', selectedCert);
                    console.log('Original file_path:', selectedCert.file_path);
                    
                    try {
                      // Use the file path directly since it should already be correct
                      const objectPath = selectedCert.file_path;
                      
                      console.log('Using object path:', objectPath);
                      
                      const { data, error } = await supabase.storage
                        .from('insurance-certificates')
                        .createSignedUrl(objectPath, 300, { download: selectedCert.file_name });
                      
                      console.log('Signed URL response:', { data, error });
                      
                      if (error) {
                        console.error('Error creating signed URL:', error);
                        toast({
                          title: "Download Error",
                          description: error.message || 'Could not create download link.',
                          variant: "destructive",
                        });
                        return;
                      }
                      
                      if (data?.signedUrl) {
                        console.log('Opening URL:', data.signedUrl);
                        // Use an anchor click to avoid popup blockers
                        const a = document.createElement('a');
                        a.href = data.signedUrl;
                        a.target = '_blank';
                        a.rel = 'noopener noreferrer';
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                      } else {
                        console.error('No signed URL received for path:', objectPath);
                        toast({
                          title: "Download Error",
                          description: `File not found at: ${objectPath}`,
                          variant: "destructive",
                        });
                      }
                    } catch (error) {
                      console.error('Unexpected error downloading certificate:', error);
                      toast({
                        title: "Download Error",
                        description: "Failed to download certificate.",
                        variant: "destructive",
                      });
                    }
                  }}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Download Certificate
                </Button>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={() => handleInsuranceApproval(selectedCert.id, 'approved')}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                <Button 
                  onClick={() => handleInsuranceApproval(selectedCert.id, 'rejected', 'Certificate does not meet requirements')}
                  variant="destructive"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}